import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Check, Loader2, ExternalLink, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import { PageHero, SectionHeader, EmeraldButton } from "@/components/Bits";
import { LIFESTYLE, ETSY_URL } from "@/lib/brand";

const formatPrice = (amount, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);

const DigitalProducts = () => {
  const [products, setProducts] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api
      .get("/products")
      .then((res) => setProducts(res.data?.products || []))
      .catch(() => toast.error("Could not load the catalog. Please refresh."));
  }, []);

  const buy = async (productId) => {
    const product = products.find((p) => p.id === productId);
    // External payment link takes precedence (Dr. Foster's own storefront)
    if (product?.payment_url) {
      window.open(product.payment_url, "_blank", "noopener,noreferrer");
      return;
    }
    setLoadingId(productId);
    try {
      const origin = window.location.origin;
      const res = await api.post("/checkout/session", {
        product_id: productId,
        origin_url: origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Checkout could not be opened. Please try again.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Checkout is temporarily unavailable. Please try again shortly.";
      toast.error(msg);
    } finally {
      setLoadingId(null);
    }
  };

  const featured = products.find((p) => p.featured);
  const others = products.filter((p) => !p.featured);

  return (
    <div data-testid="page-digital-products">
      <PageHero
        overline="Digital Products"
        title="Knowledge, beautifully packaged."
        subtitle="Premium digital resources designed to help you reclaim time, build confidence, and design your next chapter — at your own pace."
        image={LIFESTYLE.digitalEducation}
      />

      {featured && (
        <section className="ess-section bg-ess-paper">
          <div className="ess-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="relative bg-gradient-to-br from-ess-blush via-white to-ess-blush-deep">
                <img
                  src={featured.image}
                  alt={featured.name}
                  className="w-full aspect-[4/5] object-contain object-bottom"
                />
                <span className="absolute top-6 left-6 bg-ess-gold text-ess-charcoal text-[10px] uppercase tracking-[0.22em] font-semibold px-3 py-1">
                  Featured
                </span>
              </div>
            </div>
            <div className="lg:col-span-6">
              <p className="ess-overline">{featured.category}</p>
              <h2 className="font-display mt-4 text-5xl md:text-6xl text-ess-emerald leading-[1.05]">
                {featured.name}
              </h2>
              <p className="mt-3 italic text-ess-slate text-lg">
                {featured.tagline}
              </p>
              <p className="mt-6 text-ess-charcoal/85 leading-relaxed max-w-xl">
                {featured.description}
              </p>
              <ul className="mt-7 space-y-2 text-sm">
                {(featured.bullets || []).map((b) => (
                  <li key={b} className="flex items-start gap-2 text-ess-charcoal">
                    <Check size={14} className="text-ess-gold-deep mt-1" />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="mt-10 flex items-end gap-6">
                <p className="font-display text-5xl text-ess-emerald">
                  {formatPrice(featured.amount, featured.currency)}
                </p>
                <button
                  onClick={() => buy(featured.id)}
                  data-testid={`buy-${featured.id}`}
                  disabled={loadingId === featured.id}
                  className="bg-ess-emerald text-white px-7 py-4 text-[12px] uppercase tracking-[0.24em] hover:bg-ess-emerald-dark transition disabled:opacity-60 inline-flex items-center gap-3"
                >
                  {loadingId === featured.id ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Opening checkout…
                    </>
                  ) : (
                    <>
                      Buy Now <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="ess-section bg-white">
          <div className="ess-container">
            <SectionHeader
              overline="The Catalog"
              title="More from the Society."
              align="center"
              maxWidth="max-w-2xl mx-auto"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-14">
              {others.map((p) => (
                <article
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  className="bg-ess-paper group flex flex-col"
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    />
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <p className="ess-overline">{p.category}</p>
                    <h3 className="font-display text-2xl text-ess-emerald mt-2 leading-tight">
                      {p.name}
                    </h3>
                    <p className="text-ess-slate text-sm mt-3 leading-relaxed flex-1">
                      {p.description}
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                      <p className="font-display text-2xl text-ess-emerald">
                        {formatPrice(p.amount, p.currency)}
                      </p>
                      <button
                        onClick={() => buy(p.id)}
                        data-testid={`buy-${p.id}`}
                        disabled={loadingId === p.id}
                        className="text-[11px] uppercase tracking-[0.24em] text-ess-emerald hover:text-ess-gold-deep transition disabled:opacity-60 inline-flex items-center gap-2"
                      >
                        {loadingId === p.id ? (
                          <>
                            <Loader2 size={12} className="animate-spin" /> Opening…
                          </>
                        ) : (
                          <>
                            Buy Now <ShoppingBag size={12} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {!products.length && (
        <section className="ess-section bg-white">
          <div className="ess-container text-center text-ess-slate">Loading catalog…</div>
        </section>
      )}

      {/* THE EXIT STRATEGY BOUTIQUE */}
      <section className="ess-section bg-ess-blush" data-testid="products-boutique">
        <div className="ess-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 relative">
              <div className="aspect-[4/5] bg-white relative overflow-hidden shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-ess-blush via-white to-ess-blush-deep" />
                <div className="relative h-full flex flex-col items-center justify-center p-10 text-center">
                  <div className="w-20 h-20 border border-ess-gold/50 flex items-center justify-center text-ess-gold-deep">
                    <Gift size={30} strokeWidth={1.3} />
                  </div>
                  <p className="ess-overline mt-6">The Exit Strategy</p>
                  <p className="font-display text-5xl text-ess-emerald mt-2 leading-none">
                    Boutique
                  </p>
                  <span className="ess-divider mt-5" />
                  <p className="mt-6 text-ess-slate text-sm max-w-xs leading-relaxed">
                    Inspirational apparel and gift items designed for
                    professionals building lives on purpose.
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="ess-overline">The Exit Strategy Boutique</p>
              <h2 className="font-display mt-4 text-4xl md:text-5xl text-ess-emerald leading-tight">
                Wear the mindset. Gift the message.
              </h2>
              <span className="ess-divider mt-6" />
              <p className="mt-6 text-ess-slate leading-relaxed max-w-xl">
                Our Etsy boutique features inspirational apparel and thoughtful
                gift items — each piece designed to reflect the values of
                intention, empowerment, and personal design that define The
                Exit Strategy Society.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-ess-charcoal max-w-xl">
                <li className="flex items-center gap-2">
                  <Sparkles size={14} className="text-ess-gold-deep" />
                  Inspirational apparel
                </li>
                <li className="flex items-center gap-2">
                  <Gift size={14} className="text-ess-gold-deep" />
                  Meaningful gift items
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-ess-gold-deep" />
                  Handpicked designs
                </li>
                <li className="flex items-center gap-2">
                  <Check size={14} className="text-ess-gold-deep" />
                  Fulfilled by Etsy
                </li>
              </ul>
              <a
                href={ETSY_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="boutique-cta"
                className="mt-10 inline-flex items-center gap-3 bg-ess-emerald text-white px-7 py-4 text-[12px] uppercase tracking-[0.24em] hover:bg-ess-emerald-dark transition-all hover:-translate-y-[1px]"
              >
                Visit the Boutique on Etsy <ExternalLink size={14} />
              </a>
              <p className="mt-4 text-xs text-ess-slate/70">
                Opens in a new tab. Purchases are processed securely by Etsy.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="ess-section bg-ess-emerald text-white text-center">
        <div className="ess-container max-w-2xl mx-auto">
          <p className="ess-overline text-ess-gold">Coming Soon</p>
          <h2 className="font-display text-4xl md:text-5xl mt-4">
            Memberships, mastermind cohorts, and a private resource library.
          </h2>
          <p className="text-white/75 mt-5">
            Join the waitlist and be the first to receive an invitation.
          </p>
          <EmeraldButton
            as="link"
            to="/contact"
            data-testid="waitlist-cta"
            className="mt-8 bg-ess-gold text-ess-charcoal hover:bg-white"
          >
            Join the Waitlist <ArrowRight size={14} className="ml-3" />
          </EmeraldButton>
        </div>
      </section>
    </div>
  );
};

export default DigitalProducts;
